import Reveal, { Eyebrow } from "@/components/Reveal";
import { Copy, Flame, Lock, Percent, Store, Vote } from "lucide-react";
import { toast } from "sonner";

const BW_MINT = "5Vq32zsZrV8EDP5mVpYmY2HwyGvqEmZdWHn2K8eSgdWd";

const ALLOCATIONS = [
  { name: "Presale", pct: 25, tokens: 250000, vesting: "25% at TGE · 75% linear over 3 months", accent: true },
  { name: "Merchant & ecosystem incentives", pct: 20, tokens: 200000, vesting: "Linear over 36 months" },
  { name: "Team", pct: 15, tokens: 150000, vesting: "12-month cliff · linear over 24 months" },
  { name: "Treasury & development", pct: 12, tokens: 120000, vesting: "Linear over 24 months · multisig" },
  { name: "Airdrop & campaigns", pct: 10, tokens: 100000, vesting: "50% at TGE · 50% over 2 months" },
  { name: "Liquidity", pct: 8, tokens: 80000, vesting: "USDC DEX pool · LP locked 12 months with public proof" },
  { name: "Advisors & partners", pct: 5, tokens: 50000, vesting: "6-month cliff · linear over 18 months" },
  { name: "Exchange & market-making reserve", pct: 5, tokens: 50000, vesting: "Multisig · every deployment disclosed publicly" },
];

const UTILITY = [
  { icon: Percent, title: "Fee discounts & Pro tier", desc: "Holding or staking $BNW unlocks a reduced fee and Pro features." },
  { icon: Store, title: "Merchant cashback", desc: "Merchants earn $BNW cashback on processed volume, funded from the ecosystem allocation." },
  { icon: Flame, title: "Buyback programme", desc: "A share of fee revenue may buy $BNW on the open market." },
  { icon: Vote, title: "Governance", desc: "Community voting on selected parameters in a later phase." },
];

export default function Tokenomics() {
  return (
    <section id="tokenomics" className="relative py-24 sm:py-28" data-testid="tokenomics-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-12">
        <Reveal className="max-w-2xl">
          <Eyebrow>tokenomics</Eyebrow>
          <h2 className="mt-4 text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl">
            1,000,000 $BNW. Fixed. <span className="text-sgreen">Mostly vested.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-mut">
            Solana SPL token, 9 decimals. About 15% of supply circulates at launch — team and
            treasury vest for years. Unsold presale tokens are burned.
          </p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-12 rounded-2xl border border-line bg-card2 p-6 sm:p-8" data-testid="tokenomics-allocations">
            <div className="space-y-3.5">
              {ALLOCATIONS.map((a, i) => (
                <div key={a.name} data-testid={`tokenomics-row-${i}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 font-mono text-sm">
                    <span className="text-white">
                      {a.name}
                      {a.accent && <span className="ml-2 rounded-full bg-sgreen/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-sgreen">you are here</span>}
                    </span>
                    <span className="text-mut">
                      <span className="text-sgreen">{a.pct}%</span> · {a.tokens.toLocaleString()} $BNW
                    </span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/[0.06]">
                    <div
                      className={`h-full rounded-full ${a.accent ? "bg-sgreen" : "bg-white/25"}`}
                      style={{ width: `${(a.pct / 25) * 100}%` }}
                    />
                  </div>
                  <p className="mt-1.5 flex items-center gap-1.5 font-mono text-[11px] text-mut">
                    <Lock className="h-3 w-3" /> {a.vesting}
                  </p>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigator.clipboard?.writeText(BW_MINT).then(() => toast.success("Mint address copied"), () => {})}
              data-testid="tokenomics-mint-copy"
              className="group mt-7 flex w-full items-center gap-2.5 rounded-xl border border-line bg-panel px-4 py-3 text-left transition-colors hover:border-sgreen/40"
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mut">Mint</span>
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-white/80">{BW_MINT}</span>
              <Copy className="ml-auto h-4 w-4 shrink-0 text-mut group-hover:text-white" />
            </button>
          </div>
        </Reveal>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {UTILITY.map((u, i) => (
            <Reveal key={u.title} delay={0.1 + i * 0.05}>
              <div className="h-full rounded-2xl border border-line bg-card2 p-6" data-testid={`tokenomics-utility-${i}`}>
                <u.icon className="h-5 w-5 text-sgreen" />
                <h3 className="mt-4 text-base font-semibold text-white">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mut">{u.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
